import baseUrl from "./baseUrl";

/** One appointment as every list on the Today page shows it. */
export interface TodayAppointmentRow {
  appointmentId: string;
  patientId: string | null;
  patientName: string;
  phone: string;
  regNo: string;
  provisional: boolean;
  doctorId: string | null;
  doctorName: string;
  startTime: string;
  endTime: string;
  room: string;
  session: string;
  source: "appointment" | "walk_in" | "other" | "online" | string;
  status: string;
}

export interface TodayWaitingRow extends TodayAppointmentRow {
  since: string | null;
  waitedMinutes: number | null;
  longWait: boolean;
}

export interface TodayCounts {
  booked: number;
  toCome: number;
  waiting: number;
  inChair: number;
  done: number;
  noShow: number;
  cancelled: number;
  walkIn: number;
  online: number;
  other: number;
  preBooked: number;
}

export interface TodayDentistRow {
  doctorId: string;
  name: string;
  isMe: boolean;
  booked: number;
  toCome: number;
  waiting: number;
  inChair: number;
  done: number;
  noShow: number;
  next: { appointmentId: string; startTime: string; patientName: string } | null;
}

export interface TodayProvisionalPatient {
  patientId: string;
  patientName: string;
  phone: string;
  regNo: string;
  createdAt: string | null;
}

export interface TodayUnfinishedPlan {
  patientId: string;
  patientName: string;
  phone: string;
  regNo: string;
  openLines: number;
  lastVisit: string | null;
}

export interface TodayLabDue {
  labOrderId: string;
  patient: string;
  dueDate: string;
  overdueDays: number;
  orderStatus: string;
}

export interface TodayLowStockItem {
  productId: string;
  productName: string;
  availableQuantity: number;
  alertQuantity: number;
  scale: string;
}

export interface TodaySmsAlert {
  enabled: boolean;
  showAlert: boolean;
  level?: "depleted" | "low" | "campaign_skipped" | string;
  message?: string;
  tokenCredits?: number;
  lowBalanceThreshold?: number;
}

export interface TodaySubscription {
  status: "paid" | "due_soon" | "overdue" | "never_paid" | "free";
  packageName?: string;
  amount?: number;
  paymentEndDate?: string | null;
  daysToEnd?: number | null;
}

export interface TodayOwnerBlock {
  sms: TodaySmsAlert;
  /** WhatsApp / email token alerts (only channels that are low or finished). */
  channelAlerts?: TodayChannelAlert[];
  subscription: TodaySubscription;
}

export interface TodayChannelAlert {
  channel: "whatsapp" | "email";
  label: string;
  level: "depleted" | "low";
  message: string;
  tokenCredits: number;
  topUpPath: string;
}

export interface DashboardToday {
  date: string;
  tomorrow: string;
  generatedAt: string;
  longWaitMinutes: number;
  canSeeOwner: boolean;
  counts: TodayCounts;
  dentists: TodayDentistRow[];
  nextUp: TodayAppointmentRow[];
  waiting: TodayWaitingRow[];
  tomorrowList: { count: number; items: TodayAppointmentRow[] };
  treatedNotBilled: TodayAppointmentRow[];
  onlineToResolve: {
    provisionalCount: number;
    provisional: TodayProvisionalPatient[];
    unpaidOnlineToday: TodayAppointmentRow[];
  };
  unfinishedPlans: TodayUnfinishedPlan[];
  labDue: TodayLabDue[];
  lowStock: { enabled: boolean; count: number; items: TodayLowStockItem[] };
  owner: TodayOwnerBlock | null;
}

export function getDashboardToday(): Promise<DashboardToday> {
  return baseUrl.get<DashboardToday>("client/dashboard/today").then((response) => response.data);
}

export interface ConfirmSmsResponse {
  success?: boolean;
  message?: string;
  error?: string;
  sms?: { sent?: boolean };
}

/** Same call the appointment details modal makes. */
export function sendAppointmentConfirmSms(appointmentId: string): Promise<ConfirmSmsResponse> {
  return baseUrl
    .post<ConfirmSmsResponse>("/appointment/sendAppointmentConfirmSms", {}, { headers: { appointmentid: appointmentId } })
    .then((response) => response.data);
}
